/**
 * ER-6071: account invite / access status stream.
 * Builds `wss://{host}/ws/account/{accountId}/invites/?token=...` from HTTP API base URL.
 */

/** WebSocket + REST invite pipeline values (lowercase). */
export const INVITE_PIPELINE_STATUSES = Object.freeze([
  "initiated",
  "sent",
  "delivered",
  "clicked",
  "failed",
  "bounced",
]);

const INVITE_PIPELINE = new Set(INVITE_PIPELINE_STATUSES);

const ENVELOPE_WRAPPER_KEYS = Object.freeze(["message", "payload", "data", "body", "event"]);

const MERGE_USER_INVITE_FIELD_KEYS = Object.freeze(["state", "invite_status", "account_id"]);

const ENTITY_TYPE_USER = "user";

/**
 * @param {unknown} value
 * @returns {string} null/undefined become "", primitives stringify; non-primitives yield "".
 */
function stringForInviteField(value) {
  if (value == null) {
    return "";
  }
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
    case "bigint":
    case "symbol":
      return String(value);
    default:
      return "";
  }
}

function hasPresentInviteFieldValue(value) {
  return stringForInviteField(value).trim() !== "";
}

/**
 * @param {unknown} value
 * @returns {string} lowercase pipeline token or "" if not a known stage
 */
export function normalizedInvitePipelineStatus(value) {
  const inv = stringForInviteField(value).trim().toLowerCase();
  return INVITE_PIPELINE.has(inv) ? inv : "";
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isInvitePipelineStatus(value) {
  return normalizedInvitePipelineStatus(value) !== "";
}

/**
 * Overlay fields that may live on a nested envelope (WS often sends entity_type/id
 * at the top and state/invite_status inside message/payload/data).
 * Exported for unit tests and defensive reuse; callers should pass a plain object as `source`.
 * @param {object} target
 * @param {unknown} source
 */
export function mergeUserInviteEnvelopeFields(target, source) {
  if (source == null || typeof source !== "object") {
    return;
  }
  for (const k of MERGE_USER_INVITE_FIELD_KEYS) {
    const v = source[k];
    if (!hasPresentInviteFieldValue(v)) {
      continue;
    }
    target[k] = v;
  }
}

function isNonNullObject(value) {
  return value != null && typeof value === "object";
}

function isPresentEntityId(entityId) {
  return entityId != null;
}

/**
 * When `inner` is a nested user-shaped envelope, merge matching ids and recurse.
 * Otherwise merge flat fields from `inner` onto `merged`.
 * @param {object} merged
 * @param {object} inner
 */
function mergeWrapperChildIntoUserEnvelope(merged, inner) {
  const innerEt = String(inner.entity_type || "").toLowerCase();
  if (innerEt !== ENTITY_TYPE_USER || !isPresentEntityId(inner.entity_id)) {
    mergeUserInviteEnvelopeFields(merged, inner);
    return;
  }
  if (String(inner.entity_id) === String(merged.entity_id)) {
    mergeUserInviteEnvelopeFields(merged, inner);
  }
  const nested = unwrapInvitesWsPayload(inner);
  if (nested) {
    mergeUserInviteEnvelopeFields(merged, nested);
  }
}

/**
 * Top-level payload is already `entity_type: user` with `entity_id`; merge nested wrapper keys.
 * @param {object} raw
 * @returns {object}
 */
function buildMergedUserInviteEnvelope(raw) {
  const merged = { ...raw };
  for (const key of ENVELOPE_WRAPPER_KEYS) {
    const inner = raw[key];
    if (!isNonNullObject(inner)) {
      continue;
    }
    mergeWrapperChildIntoUserEnvelope(merged, inner);
  }
  return merged;
}

/**
 * Walk known wrapper keys and return first successful inner unwrap.
 * @param {object} raw
 * @returns {object|null}
 */
function findFirstUnwrappedChildPayload(raw) {
  for (const key of ENVELOPE_WRAPPER_KEYS) {
    const inner = raw[key];
    if (!isNonNullObject(inner)) {
      continue;
    }
    const found = unwrapInvitesWsPayload(inner);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Some backends wrap the event (e.g. `{ message: { entity_type, ... } }`).
 * @param {object} raw
 * @returns {object|null} inner object that looks like an invite event, or null
 */
export function unwrapInvitesWsPayload(raw) {
  if (!isNonNullObject(raw)) {
    return null;
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return unwrapInvitesWsPayload(raw[0]);
  }
  const entityType = String(raw.entity_type || "").toLowerCase();
  if (entityType === ENTITY_TYPE_USER && isPresentEntityId(raw.entity_id)) {
    return buildMergedUserInviteEnvelope(raw);
  }
  return findFirstUnwrappedChildPayload(raw);
}

export function buildAccountInvitesWebSocketUrl(httpBaseUrl, accountId, token) {
  if (!httpBaseUrl || accountId == null || !token) {
    return null;
  }
  let parsed;
  try {
    parsed = new URL(httpBaseUrl);
  } catch {
    /* Invalid base URL */
    return null;
  }
  const wsProto = parsed.protocol === "https:" ? "wss:" : "ws:";
  const safeToken = encodeURIComponent(String(token));
  return `${wsProto}//${parsed.host}/ws/account/${encodeURIComponent(
    String(accountId)
  )}/invites/?token=${safeToken}`;
}

/**
 * @param {unknown} stateRaw
 * @returns {string}
 */
function normalizeWsState(stateRaw) {
  if (stateRaw == null) {
    return "";
  }
  switch (typeof stateRaw) {
    case "string":
    case "number":
    case "boolean":
    case "bigint":
    case "symbol":
      return String(stateRaw).trim().toLowerCase().replaceAll("_", " ");
    default:
      return "";
  }
}

/**
 * @param {string} state
 * @param {boolean} isSuspended
 * @param {string} inviteStatus
 * @returns {{ state: string, is_suspended: boolean, invite_status: string }}
 */
function inviteAccessSnapshot(state, isSuspended, inviteStatus = "") {
  return { state, is_suspended: isSuspended, invite_status: inviteStatus };
}

/**
 * Maps a websocket payload to fields stored on an account access list row (`user`, `is_suspended`).
 * @param {unknown} payload
 * @returns {{ state: string, is_suspended: boolean, invite_status: string } | null}
 */
export function mapInviteWsPayloadToAccessFields(payload) {
  if (
    payload == null ||
    typeof payload !== "object" ||
    String(payload.entity_type || "").toLowerCase() !== ENTITY_TYPE_USER
  ) {
    return null;
  }
  const s = normalizeWsState(payload.state);
  const inv = normalizedInvitePipelineStatus(payload.invite_status);

  if (s === "has access" || s === "has_access") {
    return inviteAccessSnapshot("operational", false);
  }
  if (s === "invited") {
    return inviteAccessSnapshot("invited", false, inv);
  }
  if (s === "suspended") {
    return inviteAccessSnapshot("operational", true);
  }
  if (s === "blocked") {
    return inviteAccessSnapshot("blocked", false);
  }
  if (s === "closed") {
    return inviteAccessSnapshot("closed", false);
  }
  // Pipeline events often send only `invite_status` (no `state`) once the user is already invited.
  if (s === "" && inv) {
    return inviteAccessSnapshot("invited", false, inv);
  }
  return null;
}
